import { useState } from 'react'
import { RefreshCw, Undo2 } from 'lucide-react'
import { useArticleModeration } from '@/features/moderation/useArticleModeration'
import { DiscardDraftAlert } from '@/features/moderation/DiscardDraftAlert'
import { ChangesFrame } from '@/features/editor/components/publish/ChangesFrame'
import { BusyButton } from '@/features/editor/components/publish/BusyButton'
import { useArticleUpdate } from './useArticleUpdate'
import { useMessages } from '@/i18n'
import { messages } from './messages'
import { messages as publishMessages } from '@/features/editor/components/publish/messages'

interface Props {
  /** `null` — статья ещё не создана. */
  articleId: number | null
  /** Несохранённая форма: на проверку ушла бы не та версия, что видит автор. */
  dirty: boolean
  /** Статья на проверке (`useIsArticleFrozen` владельца) — обе кнопки погашены. */
  frozen: boolean
}

/**
 * Карточка «Есть неопубликованные изменения» во вкладке «Доступ» — как секция
 * изменений в окне публикации курса: в редакторе статья только сохраняется,
 * публикуется и обновляется здесь. Видна лишь у живой статьи с правками, которых
 * читатели ещё не видят. Под проверкой (`pending`) обе кнопки погашены — как и вся форма.
 */
export const ArticleChangesSection = ({ articleId, dirty, frozen }: Props) => {
  const m = useMessages(messages)
  // Подписи прогресса — общие с секцией изменений курса.
  const pm = useMessages(publishMessages)
  const { state } = useArticleModeration(articleId)
  const update = useArticleUpdate(articleId)
  const [discardOpen, setDiscardOpen] = useState(false)
  // Крутилка — на той кнопке, которую нажали.
  const [action, setAction] = useState<'update' | 'discard' | null>(null)
  // Под проверкой карточку рисует сама проверка (`ArticleReviewPanel`): вторая — дубль.
  if (
    !state ||
    state.visibility === 'unpublished' ||
    !state.hasUnsubmittedChanges ||
    state.review?.status === 'pending'
  )
    return null
  const busyFor = (a: 'update' | 'discard'): boolean => update.isBusy && action === a
  const locked = update.isBusy || frozen
  // `429` — бизнес-антиспам: отсчёт прямо на кнопке, без тоста.
  const retry = update.retryLabel

  // Ширина — как у селекта доступа под карточкой (та же сетка в две колонки).
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <ChangesFrame live note={m.liveChanges}>
        <div className="flex flex-col gap-2">
          <BusyButton
            busy={busyFor('update')}
            icon={RefreshCw}
            label={retry ? m.retryIn(retry) : m.update}
            busyLabel={pm.updating}
            disabled={locked || dirty || retry !== null}
            title={dirty ? m.saveChangesFirst : undefined}
            onClick={() => {
              setAction('update')
              void update.submit()
            }}
          />
          <BusyButton
            variant="secondary"
            // Своя заливка, как у курса: штатная `secondary` сливалась с фоном карточки.
            className="bg-foreground/10 text-foreground hover:bg-foreground/[0.18]"
            busy={busyFor('discard')}
            icon={Undo2}
            label={m.discardChanges}
            busyLabel={pm.discarding}
            disabled={locked}
            onClick={() => setDiscardOpen(true)}
          />
        </div>
        <DiscardDraftAlert
          open={discardOpen}
          onOpenChange={setDiscardOpen}
          onConfirm={() => {
            setAction('discard')
            void update.discard()
          }}
          subject={m.discardSubject}
          note={
            // `rebaseArticleForm` оставляет разошедшиеся поля авторскими — форма их не теряет.
            dirty ? <p className="text-sm text-muted-foreground">{m.discardDirtyNote}</p> : null
          }
        />
      </ChangesFrame>
    </div>
  )
}
